/**
 * This hook can easily be refactore to use among other tables that need to be filterable and have URL persistence.
 * It would only require a small refactor to dynamically to use a Generic Constraint to define the sort keys, orders, default filters and possibly callbacks
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import qs from 'query-string';
import { SortKey } from './useGetTableColumns';

export interface NewsTableFilters {
    orderBy: SortKey;
    sortOrder: 'asc' | 'desc';
    searchQuery?: string;
    tagsIds?: number[];
}

const DEFAULT_FILTERS: NewsTableFilters = {
    orderBy: 'publishedDate',
    sortOrder: 'desc',
    searchQuery: '',
    tagsIds: []
};

const VALID_SORT_KEYS: SortKey[] = ['title', 'description', 'publishedDate'];
const VALID_SORT_ORDERS = ['asc', 'desc'] as const;

interface UseNewsTableFiltersReturn {
    filters: NewsTableFilters;
    updateFilters: (newFilters: Partial<NewsTableFilters>) => void;
    onSort: (columnKey: string, nextOrder: string) => void;
}

function getTagsIds(tagsIds: string): number[] {
    if (tagsIds === '') return [];

    if (tagsIds.includes(',')) {
        return tagsIds.split(',').map((id) => Number.parseInt(id, 10));
    }

    return [Number.parseInt(tagsIds, 10)];
}

/**
 * Custom hook to manage table filters with URL persistence
 *
 * Features:
 * - Reads initial state from URL query parameters on mount
 * - Adds default filters to URL if no parameters are present
 * - Syncs filter changes to URL for bookmarkable/shareable links
 * - Provides validation for filter values
 * - Returns onSort callback for table column integration
 *
 */
export default function useNewsTableFilters(): UseNewsTableFiltersReturn {
    const navigate = useNavigate();
    const location = useLocation();

    const getInitialFilters = useCallback((): NewsTableFilters => {
        const queryParams = qs.parse(location.search);

        const orderBy =
            typeof queryParams.orderBy === 'string' && VALID_SORT_KEYS.includes(queryParams.orderBy as SortKey)
                ? (queryParams.orderBy as SortKey)
                : DEFAULT_FILTERS.orderBy;

        const sortOrder =
            typeof queryParams.sortOrder === 'string' && VALID_SORT_ORDERS.includes(queryParams.sortOrder as any)
                ? (queryParams.sortOrder as 'asc' | 'desc')
                : DEFAULT_FILTERS.sortOrder;

        const searchQuery = typeof queryParams.searchQuery === 'string' ? queryParams.searchQuery : DEFAULT_FILTERS.searchQuery;

        const tagsIds = typeof queryParams.tagsIds === 'string' ? getTagsIds(queryParams.tagsIds) : DEFAULT_FILTERS.tagsIds;

        return { orderBy, sortOrder, searchQuery, tagsIds };
    }, [location.search]);

    const [filters, setFilters] = useState<NewsTableFilters>(getInitialFilters);

    const syncFiltersToUrl = useCallback(
        (newFilters: NewsTableFilters) => {
            const tagsIds = !newFilters?.tagsIds?.length ? undefined : newFilters.tagsIds.join(',');
            const searchQuery = !newFilters?.searchQuery?.length ? undefined : newFilters.searchQuery;

            const updatedQuery = {
                orderBy: newFilters.orderBy,
                sortOrder: newFilters.sortOrder,
                ...(searchQuery && { searchQuery }),
                ...(tagsIds && { tagsIds })
            };

            const newSearch = qs.stringify(updatedQuery);
            const newUrl = `${location.pathname}?${newSearch}`;

            navigate(newUrl, { replace: true });
        },
        [navigate, location.pathname]
    );

    useEffect(() => {
        const queryParams = qs.parse(location.search);
        const hasFilterParams = queryParams.orderBy || queryParams.sortOrder || queryParams.searchQuery || queryParams.tagsIds;

        if (!hasFilterParams) {
            syncFiltersToUrl(filters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateFilters = useCallback(
        (newFilters: Partial<NewsTableFilters>) => {
            const updatedFilters = { ...filters, ...newFilters };
            setFilters(updatedFilters);
            syncFiltersToUrl(updatedFilters);
        },
        [filters, syncFiltersToUrl]
    );

    const onSort = useCallback(
        (columnKey: string, nextOrder: string) => {
            if (VALID_SORT_KEYS.includes(columnKey as SortKey) && VALID_SORT_ORDERS.includes(nextOrder as any)) {
                updateFilters({
                    orderBy: columnKey as SortKey,
                    sortOrder: nextOrder as 'asc' | 'desc'
                });
            }
        },
        [updateFilters]
    );

    return {
        filters,
        updateFilters,
        onSort
    };
}
